import { useEffect, useState } from "react";
import {
  getHeadersForRequest,
  numberWithCommas,
  numberWithCommas2,
} from "../utils/functions";
import axios from "axios";
import { ProductionList } from "../utils/types";
import {
  Button,
  Card,
  Center,
  Checkbox,
  Collapse,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightAddon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Table,
  TableCaption,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useBreakpointValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import Nav from "../components/navbar";
import { Loading } from "../components/Loading";
import { useNavigate } from "react-router-dom";
import useCustomTranslation from "../hooks/useCustomTranslation";
import moment from "moment";
import { AddIcon } from "@chakra-ui/icons";
import ProductionHeatmapApex from "../components/analytics/ProductionHeatmap";
import { ProductsValues } from "../utils/strings";
import AppContainer from "../components/AppContainer";

const ProductionScreen = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAddMode, setIsAddMode] = useState<boolean>(false);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [modalIsLoading, setModalIsLoading] = useState<boolean>(false);
  const [selectedSession, setSelectedSession] = useState<ProductionList>();
  const [sessions, setSessions] = useState<ProductionList[]>([]);
  const [filteredSessions, setFilteredSessions] = useState<ProductionList[]>(
    []
  );
  const [date, setDate] = useState<string>(moment().format("YYYY-MM-DD")); // Default to current time
  const [item, setItem] = useState<string>("Potato Crisps");
  const [rawMaterialWeight, setRawMaterialWeight] = useState<number>(0);
  const [finishedMaterialWeight, setFinishedMaterialWeight] =
    useState<number>(0); // Default to today’s date
  const [burntWeight, setBurntWeight] = useState<number>(0);
  const { isOpen, onToggle } = useDisclosure();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [count, setCount] = useState(0);

  const { t, changeLanguage } = useCustomTranslation();
  const toast = useToast();

  const [forceFit, setForceFit] = useState<boolean>(false);

  const isSmallScreen = useBreakpointValue({ base: true, lg: forceFit }); // < md

  useEffect(() => {
    setIsLoading(true);
    getSessions();
  }, []);

  useEffect(() => {
    setIsLoading(true);
    filterSessions();
  }, [searchTerm, sessions]);

  const getSessions = async () => {
    const headers = await getHeadersForRequest();
    try {
      const response = await axios.get<ProductionList[]>(
        `${process.env.REACT_APP_TEST_API_URL}/production/getAll`,
        { headers }
      );

      // console.log(JSON.stringify(response));
      // Sort filtered customers by balance in descending order
      const sortedFilteredData = response.data.sort(
        (a, b) => Number(b.id) - Number(a.id)
      );
      setSessions(sortedFilteredData);
      setCount(1);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setModalIsLoading(true);

    const headers = await getHeadersForRequest();
    await axios
      .post(
        `${process.env.REACT_APP_TEST_API_URL}/production/saveSession`,
        {
          date: date,
          item: item,
          rawMaterialWeight: rawMaterialWeight,
          finishedMaterialWeight: finishedMaterialWeight,
          burntWeight: burntWeight,
        },
        { headers }
      )
      .then((response) => {
        toast({
          title: t("Saved successfully"),
          duration: 3000,
          status: "success",
        });
        setModalIsLoading(false);
        // console.log(response);
        setIsAddMode(false);
        getSessions();
        resetForm();
      })
      .catch((error) => {
        console.error(error);
        setModalIsLoading(false);
        toast({
          title: t("Error saving"),
          duration: 3000,
          status: "error",
        });
      });
  };

  const handleUpdate = async () => {
    setModalIsLoading(true);

    const headers = await getHeadersForRequest();
    await axios
      .post(
        `${process.env.REACT_APP_TEST_API_URL}/production/updateDetails`,
        {
          sessionId: selectedSession?.id,
          finishedMaterialWeight: finishedMaterialWeight,
          burntWeight: burntWeight,
        },
        { headers }
      )
      .then((response) => {
        toast({
          title: t("Saved successfully"),
          duration: 3000,
          status: "success",
        });
        setModalIsLoading(false);
        // console.log(response);
        setShowDetailsModal(false);
        getSessions();
        resetForm();
      })
      .catch((error) => {
        console.error(error);
        setModalIsLoading(false);
        toast({
          title: t("Error saving"),
          duration: 3000,
          status: "error",
        });
      });
  };

  const resetForm = () => {
    setDate(moment().format("YYYY-MM-DD"));
    setItem("");
    setRawMaterialWeight(0);
    setFinishedMaterialWeight(0);
    setBurntWeight(0);
  };

  const filterSessions = () => {
    setIsLoading(true);
    if (!searchTerm) {
      setFilteredSessions(sessions);
      if (count === 1) {
        setIsLoading(false);
      }
    } else {
      const lowercasedFilter = searchTerm.toLowerCase();
      const filteredData =
        sessions !== null
          ? sessions.filter((session) =>
              Object.keys(session).some((key: any) => {
                const value = session[key as keyof ProductionList];
                return (
                  value !== null &&
                  value.toString().toLowerCase().includes(lowercasedFilter)
                );
              })
            )
          : [];
      // Sort filtered customers by balance in descending order
      const sortedFilteredData = filteredData.sort(
        (a, b) => Number(b.id) - Number(a.id)
      );
      setFilteredSessions(sortedFilteredData);
      setIsLoading(false);
    }
  };

  return (
    <AppContainer pageName="Production Sessions">
      {/* <Stack position="sticky" top={0} zIndex={1} width="full">
        <Nav pageName={"Production Sessions"} />
      </Stack> */}
      <Stack px={1} pb={10}>
        {isLoading ? (
          <Center h={600}>
            <Loading />
          </Center>
        ) : (
          <Stack>
            <ProductionHeatmapApex />

            <HStack
              alignItems={"center"}
              justifyContent={"space-between"}
              flexWrap={"wrap"}
            >
              <InputGroup maxW={500}>
                <Input
                  placeholder={t("Search for a session...")}
                  width="100%"
                  borderRadius="4"
                  py="3"
                  px="1"
                  fontSize="14"
                  onChange={(text) => setSearchTerm(text.target.value)}
                  autoCapitalize="none"
                />
                <InputRightAddon>{t("Search")}</InputRightAddon>
              </InputGroup>

              <Checkbox 
              isChecked={forceFit}
              onChange={(e) => setForceFit(e.target.checked ? true : false)}
              >
                Small Table
              </Checkbox>
            </HStack>

            <Tooltip hasArrow label="Add New Session">
              <IconButton
                position={"fixed"}
                right={5}
                bottom={isSmallScreen ? 20 : 5}
                onClick={() => setIsAddMode(true)}
                // onClick={()=> onToggle}
                isRound={true}
                variant="solid"
                colorScheme="blue"
                aria-label="Done"
                fontSize="20px"
                icon={<AddIcon />}
                onMouseEnter={onToggle}
                onMouseLeave={onToggle}
              />
            </Tooltip>

            {/* {isHovered && ( */}
            {/* <Collapse in={isOpen} animateOpacity>
              <Card
                position={"fixed"}
                right={20}
                bottom={7}
                bg="blue.600"
                px={3}
                py={1}
              >
                <Text color={"white"}>Add New Session</Text>
              </Card>
            </Collapse> */}
            {/* )} */}

            {filteredSessions.length > 0 ? (
              <TableContainer>
                <Table size={isSmallScreen ? "sm" : "md"} variant="striped">
                  <TableCaption>{t("List of Sessions")}</TableCaption>
                  <Thead>
                    <Tr>
                      <Th>{t("Session")}</Th>
                      <Th>{t("Session Date")}</Th>
                      <Th>{t("Item")}</Th>
                      <Th isNumeric>{t("Raw M.")}</Th>
                      <Th isNumeric>{t("Finished G.")}</Th>
                      <Th isNumeric>{t("Usable")}</Th>
                      <Th isNumeric>{t("Burnt")}</Th>
                      <Th isNumeric>{t("Est. Value")}</Th>
                      <Th isNumeric>{t("Output %")}</Th>
                      <Th isNumeric>{t("Usable %")}</Th>
                      <Th isNumeric>{t("Waste %")}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredSessions.map(
                      (session: ProductionList, index: number) => (
                        <Tr
                          onClick={() => {
                            if (
                              Math.round(session.finishedMaterialWeight) === 0
                            ) {
                              resetForm();
                              setSelectedSession(session);
                              setShowDetailsModal(true);
                            }
                          }}
                        >
                          <Td>{session.id} </Td>
                          <Td>{moment(session.date).format("DD MMM YYYY")}</Td>
                          <Td maxW={"max-content"}>{session.item}</Td>
                          <Td isNumeric>
                            {session.item === "Banana Crisps" ||
                            session.item === "Ndizi Mzuzu Crisps" ||
                            session.item === "Cassava Crisps" ||
                            session.item === "Magimbi Crisps"
                              ? session.rawMaterialWeight
                              : numberWithCommas2(
                                  session.rawMaterialWeight
                                )}{" "}
                            {session.item === "Banana Crisps" ||
                            session.item === "Ndizi Mzuzu Crisps"
                              ? "Kungu"
                              : session.item === "Cassava Crisps" ||
                                session.item === "Magimbi Crisps"
                              ? "Pcs"
                              : "KG"}
                          </Td>
                          <Td isNumeric>
                            {numberWithCommas2(session.finishedMaterialWeight)}{" "}
                            KG
                          </Td>
                          <Td isNumeric>
                            {numberWithCommas2(
                              session.finishedMaterialWeight -
                                session.burntWeight
                            )}{" "}
                            KG
                          </Td>
                          <Td isNumeric>{numberWithCommas2(session.burntWeight)} KG</Td>
                          <Td isNumeric>
                            {numberWithCommas(
                              Math.round(
                                (session.finishedMaterialWeight -
                                  session.burntWeight) *
                                  (session.item === "Cashewnuts"
                                    ? 27000
                                    : session.item === "Ndizi Mzuzu Crisps"
                                    ? 19230
                                    : 18000)
                              )
                            )}{" "}
                            Tsh
                          </Td>
                          <Td isNumeric>
                            {session.item !== "Banana Crisps" &&
                            session.item !== "Cassava Crisps"
                              ? (
                                  (session.finishedMaterialWeight /
                                    session.rawMaterialWeight) *
                                  100
                                ).toFixed(2)
                              : numberWithCommas2(100.00)}{" "}
                            %
                          </Td>
                          <Td isNumeric>
                            {session.item !== "Banana Crisps" &&
                            session.item !== "Cassava Crisps"
                              ? (
                                  ((session.finishedMaterialWeight -
                                    session.burntWeight) /
                                    session.rawMaterialWeight) *
                                  100
                                ).toFixed(2)
                              : (
                                  ((session.finishedMaterialWeight -
                                    session.burntWeight) /
                                    session.finishedMaterialWeight) *
                                  100
                                ).toFixed(2)}{" "}
                            %
                          </Td>
                          <Td isNumeric>
                            {session.item !== "Banana Crisps" &&
                            session.item !== "Cassava Crisps"
                              ? (
                                  (session.burntWeight /
                                    session.rawMaterialWeight) *
                                  100
                                ).toFixed(2)
                              : (
                                  (session.burntWeight /
                                    session.finishedMaterialWeight) *
                                  100
                                ).toFixed(2)}{" "}
                            %
                          </Td>
                        </Tr>
                      )
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            ) : (
              <Stack alignItems={"center"} justifyContent={"center"} h={400}>
                <Text
                  fontSize={20}
                  textAlign={"center"}
                  // color={primary}
                >
                  {t("There are no Sessions")}
                </Text>
                <Text
                  fontSize={20}
                  textAlign={"center"}
                  // color={primary}
                >
                  Click the + button to add one!
                </Text>
              </Stack>
            )}
          </Stack>
        )}

        {/* ADD SESSION MODAL */}
        <Modal
          isCentered
          closeOnOverlayClick={false}
          isOpen={isAddMode}
          onClose={() => {
            setIsAddMode(false);
            getSessions();
          }}
        >
          <ModalOverlay
            bg="blackAlpha.300"
            backdropFilter="blur(10px) hue-rotate(90deg)"
          />
          <ModalContent maxWidth="350">
            <ModalCloseButton />
            <ModalHeader>{t("Add Session")}</ModalHeader>
            <ModalBody>
              {modalIsLoading ? (
                <Loading />
              ) : (
                <Stack flex={1}>
                  <Stack alignItems={"center"}>
                    <Stack>
                      <Text>Date*</Text>
                      <Input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                      />

                      {/* <Text>Item*</Text>
                      <Input
                        value={item}
                        onChange={(e) => setItem(e.target.value)}
                      /> */}

                      <Stack>
                        <Text>Item*</Text>
                        <Stack>
                          <Select
                            onChange={(e) => setItem(e.target.value)}
                            // borderRadius={30}
                            // borderWidth={3}
                          >
                            {ProductsValues.map((value) => (
                              <option value={value} key={value}>
                                {value.toString()}
                              </option>
                            ))}
                          </Select>
                        </Stack>
                      </Stack>

                      <Text>Raw Material in KG*</Text>
                      <Input
                        type="number"
                        value={rawMaterialWeight}
                        onChange={(e) =>
                          setRawMaterialWeight(parseFloat(e.target.value))
                        }
                      />

                      <Text>Finished Product in KG (inclusive of burnt)</Text>
                      <Input
                        type="number"
                        value={finishedMaterialWeight}
                        onChange={(e) =>
                          setFinishedMaterialWeight(parseFloat(e.target.value))
                        }
                      />

                      <Text>Burnt in KG</Text>
                      <Input
                        type="number"
                        value={burntWeight}
                        onChange={(e) =>
                          setBurntWeight(parseFloat(e.target.value))
                        }
                      />

                      <br />

                      <Button
                        onClick={handleSave}
                        // onClick={()=>console.log(sessions.length)}
                        isLoading={isLoading}
                        colorScheme="blue"
                      >
                        Submit
                      </Button>

                      <br />
                    </Stack>
                  </Stack>
                </Stack>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* UPDATE SESSION MODAL */}
        <Modal
          isCentered
          closeOnOverlayClick={false}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            getSessions();
          }}
        >
          <ModalOverlay
            bg="blackAlpha.300"
            backdropFilter="blur(10px) hue-rotate(90deg)"
          />
          <ModalContent maxWidth="350">
            <ModalCloseButton />
            <ModalHeader>{t("Update Details")}</ModalHeader>
            <ModalBody>
              {modalIsLoading ? (
                <Loading />
              ) : (
                <Stack flex={1}>
                  <Stack alignItems={"center"}>
                    <Stack>
                      <Text>Date*</Text>
                      <Input
                        isDisabled
                        type="date"
                        value={moment(selectedSession?.date).format(
                          "YYYY-MM-DD"
                        )}
                        onChange={(e) => setDate(e.target.value)}
                      />

                      <Stack>
                        <Text>Item*</Text>
                        <Stack>
                          <Select
                            onChange={(e) => setItem(e.target.value)}
                            // borderRadius={30}
                            // borderWidth={3}
                          >
                            {ProductsValues.map((value) => (
                              <option value={value} key={value}>
                                {value.toString()}
                              </option>
                            ))}
                          </Select>
                        </Stack>
                      </Stack>

                      <Text>Raw Material in KG*</Text>
                      <Input
                        isDisabled
                        type="number"
                        value={selectedSession?.rawMaterialWeight}
                        onChange={(e) =>
                          setRawMaterialWeight(parseFloat(e.target.value))
                        }
                      />

                      <Text>Finished Product in KG* (inclusive of burnt)</Text>
                      <Input
                        type="number"
                        value={finishedMaterialWeight}
                        onChange={(e) =>
                          setFinishedMaterialWeight(parseFloat(e.target.value))
                        }
                      />

                      <Text>Burnt in KG</Text>
                      <Input
                        type="number"
                        value={burntWeight}
                        onChange={(e) =>
                          setBurntWeight(parseFloat(e.target.value))
                        }
                      />

                      <br />

                      <Button
                        onClick={handleUpdate}
                        // onClick={()=>console.log(selectedSession)}
                        isLoading={isLoading}
                        colorScheme="blue"
                      >
                        Submit
                      </Button>

                      <br />
                    </Stack>
                  </Stack>
                </Stack>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </Stack>
    </AppContainer>
  );
};

export default ProductionScreen;
